import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, firstValueFrom, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import type {
  AcceptInviteRequest,
  InvitePreview,
  RefreshResponse,
  Role,
  SignInResponse,
  User,
} from '../models/auth.model';
import { PlatformService } from './platform.service';
import { SKIP_AUTH_REFRESH } from '../auth/auth.context';

const AT_KEY = 'loggic.at';
const RT_KEY = 'loggic.rt';

/**
 * Autenticação contra a API da plataforma ativa (não há back centralizado).
 * Estado em signals; tokens em localStorage. Sessão única: um novo login
 * em outro lugar invalida o refresh anterior → 401 no refresh encerra a sessão.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly platform = inject(PlatformService);
  private readonly router = inject(Router);

  private readonly _user = signal<User | null>(null);
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly role = computed<Role | null>(() => this._user()?.role ?? null);

  /** Refresh compartilhado em voo: requisições concorrentes reaproveitam o mesmo. */
  private refresh$: Observable<string> | null = null;

  private base(): string {
    return this.platform.apiBaseUrl();
  }

  get accessToken(): string | null {
    return localStorage.getItem(AT_KEY);
  }

  get refreshToken(): string | null {
    return localStorage.getItem(RT_KEY);
  }

  private storeTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(AT_KEY, accessToken);
    localStorage.setItem(RT_KEY, refreshToken);
  }

  private clearTokens(): void {
    localStorage.removeItem(AT_KEY);
    localStorage.removeItem(RT_KEY);
  }

  // --- Login ---------------------------------------------------------------

  signIn(email: string, password: string): Observable<User> {
    const ctx = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    return this.http
      .post<SignInResponse>(`${this.base()}/api/loggic/auth/sign-in`, { email, password }, { context: ctx })
      .pipe(
        tap((res) => {
          this.storeTokens(res.accessToken, res.refreshToken);
          this._user.set(res.user);
        }),
        map((res) => res.user),
      );
  }

  // --- Convite (público, sem refresh) -------------------------------------

  /** Prévia do convite para a tela de definir senha. */
  invitePreview(token: string): Observable<InvitePreview> {
    const ctx = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    return this.http.get<InvitePreview>(`${this.base()}/api/loggic/auth/invite/${token}`, { context: ctx });
  }

  /** Define a senha, ativa a conta e já devolve a sessão (auto-login). */
  acceptInvite(token: string, password: string): Observable<User> {
    const ctx = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    const body: AcceptInviteRequest = { token, password };
    return this.http
      .post<SignInResponse>(`${this.base()}/api/loggic/auth/accept-invite`, body, { context: ctx })
      .pipe(
        tap((res) => {
          this.storeTokens(res.accessToken, res.refreshToken);
          this._user.set(res.user);
        }),
        map((res) => res.user),
      );
  }

  // --- Refresh (compartilhado) --------------------------------------------

  /**
   * Renova o access token. Chamadas concorrentes durante um 401 recebem o
   * MESMO Observable (fila) — dispara um único POST /refresh-token.
   */
  refreshAccessToken(): Observable<string> {
    if (this.refresh$) return this.refresh$;

    const token = this.refreshToken;
    if (!token) {
      this.forceLogout();
      return throwError(() => new Error('no-refresh-token'));
    }

    const ctx = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    this.refresh$ = this.http
      .post<RefreshResponse>(`${this.base()}/api/loggic/auth/refresh-token`, { refreshToken: token }, { context: ctx })
      .pipe(
        tap((res) => this.storeTokens(res.accessToken, res.refreshToken)),
        map((res) => res.accessToken),
        catchError((err) => {
          // Refresh inválido/expirado (sessão única encerrada) → logout local.
          this.forceLogout();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refresh$ = null;
        }),
        shareReplay(1),
      );

    return this.refresh$;
  }

  // --- Perfil --------------------------------------------------------------

  fetchMe(): Observable<User> {
    return this.http.get<User>(`${this.base()}/api/loggic/auth/me`).pipe(tap((user) => this._user.set(user)));
  }

  // --- Bootstrap (provideAppInitializer) -----------------------------------

  /**
   * Reidrata a sessão no arranque: se há token, chama /me; em 401 tenta UM
   * refresh e refaz /me; se falhar, limpa tokens (guards levam a /login).
   */
  async bootstrap(): Promise<void> {
    if (!this.accessToken || !this.base()) return;

    try {
      await firstValueFrom(this.fetchMe());
    } catch {
      try {
        await firstValueFrom(this.refreshAccessToken().pipe(switchMap(() => this.fetchMe())));
      } catch {
        this.clearSession();
      }
    }
  }

  // --- Logout --------------------------------------------------------------

  /** Logout explícito (não há endpoint de logout): descarta tokens e vai a /login. */
  logout(): void {
    this.clearSession();
    void this.router.navigate(['/login']);
  }

  /** Usado internamente quando o refresh falha (sessão encerrada pelo back). */
  forceLogout(): void {
    this.clearSession();
    void this.router.navigate(['/login']);
  }

  clearSession(): void {
    this.clearTokens();
    this._user.set(null);
  }

  /** Garante user carregado para os guards (usado no CanActivate). */
  ensureUser(): Observable<User | null> {
    if (this._user()) return of(this._user());
    if (!this.accessToken || !this.base()) return of(null);
    return this.fetchMe().pipe(
      map((u) => u as User | null),
      catchError(() => of(null)),
    );
  }
}
